<template>
  <div class="calculations-progress" data-testid="calculations-progress-view">
    <div class="progress-header">
      <h3>{{ t('Processando Calculos') }}</h3>
      <p class="progress-description">{{ t('Aguarde enquanto os calculos sao processados...') }}</p>
    </div>

    <div class="steps-progress">
      <div
        v-for="(step, index) in steps"
        :key="step.key"
        class="step-item"
        :class="{
          'completed': step.status === 'completed',
          'running': step.status === 'running',
          'pending': step.status === 'pending',
          'error': step.status === 'error'
        }"
      >
        <div class="step-icon">
          <i v-if="step.status === 'completed'" class="pi pi-check-circle text-green-500" />
          <ProgressSpinner
            v-else-if="step.status === 'running'"
            style="width: 24px; height: 24px;"
          />
          <i v-else-if="step.status === 'error'" class="pi pi-times-circle text-red-500" />
          <i v-else class="pi pi-circle text-gray-400" />
        </div>
        <div class="step-content">
          <span class="step-title">{{ step.title }}</span>
          <span v-if="step.status === 'running'" class="step-message">{{ step.loadingText }}</span>
          <span v-else-if="step.status === 'completed' && step.result" class="step-result">
            {{ n(step.result, 'currency') }}
          </span>
          <span v-else-if="step.status === 'error'" class="step-error text-red-500">
            {{ step.errorMessage || t('Erro ao processar') }}
          </span>
        </div>
      </div>
    </div>

    <div class="progress-summary">
      <div class="progress-stats">
        <span>{{ completedCount }} {{ t('de') }} {{ steps.length }} {{ t('concluido') }}</span>
      </div>
      <progress-bar :value="progressPercentage" class="progress-bar" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

type StepStatus = 'pending' | 'running' | 'completed' | 'error';

interface CalculationStep {
  key: string;
  title: string;
  loadingText: string;
  status: StepStatus;
  result: number | null;
  errorMessage: string | null;
}

const { t, n } = useI18n();

const props = defineProps<{
  steps: CalculationStep[];
}>();

const completedCount = computed((): number => {
  return props.steps.filter(s => s.status === 'completed').length;
});

const progressPercentage = computed((): number => {
  if (props.steps.length === 0) return 0;
  return Math.round((completedCount.value / props.steps.length) * 100);
});
</script>

<style scoped>
.calculations-progress {
  padding: 1.5rem;
}

.progress-header {
  text-align: center;
  margin-bottom: 2rem;
}

.progress-header h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  color: var(--text-color);
}

.progress-description {
  margin: 0;
  color: var(--text-color-secondary);
}

.steps-progress {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background-color: var(--surface-50);
  border: 1px solid var(--surface-border);
  transition: all 0.3s ease;
}

.step-item.running {
  background-color: var(--primary-50);
  border-color: var(--primary-200);
}

.step-item.completed {
  background-color: var(--green-50);
  border-color: var(--green-200);
}

.step-item.error {
  background-color: var(--red-50);
  border-color: var(--red-200);
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.step-icon i {
  font-size: 1.25rem;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.step-title {
  font-weight: 600;
  color: var(--text-color);
}

.step-message {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.step-result {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--green-600);
}

.step-error {
  font-size: 0.875rem;
}

.progress-summary {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.progress-stats {
  display: flex;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.progress-bar {
  height: 8px;
}

.progress-bar :deep(.p-progressbar-value) {
  background: var(--primary-color);
}
</style>
